import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { SignedAvatar } from '@/components/ui/signed-avatar';
import { MentionInput, renderCommentWithMentions } from '@/components/ui/mention-input';
import {
  MessageCircle,
  Send,
  CornerDownRight,
  X,
  Pencil,
  Trash2,
  Check,
  SmilePlus
} from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import { captureEvent } from '@/lib/posthog';
import { toast } from 'sonner';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const AVAILABLE_EMOJIS = ['👍', '❤️', '🎉', '👏', '💪'];

interface Reaction {
  id: string;
  emoji: string;
  user_id: string;
}

interface Comment {
  id: string;
  content: string;
  user_id: string;
  parent_id: string | null;
  created_at: string;
  updated_at: string;
  mentions?: string[];
  profile?: {
    full_name: string | null;
    avatar_url: string | null;
  };
  replies?: Comment[];
}

interface TeamMember {
  user_id: string;
  full_name: string | null;
  avatar_url: string | null;
}

interface TaskReactionsCommentsProps {
  executionId: string;
  patientId?: string;
  compact?: boolean;
  autoOpen?: boolean;
  highlightCommentId?: string | null;
  onCommentsCountChange?: (count: number) => void;
  // Preloaded counts to avoid initial fetch
  initialReactionsCount?: number;
  initialCommentsCount?: number;
}

export function TaskReactionsComments({
  executionId,
  patientId,
  compact = false,
  autoOpen = false,
  highlightCommentId,
  onCommentsCountChange,
  initialReactionsCount,
  initialCommentsCount,
  initialReactions
}: TaskReactionsCommentsProps & { initialReactions?: Reaction[] }) {
  const { user } = useAuth();
  const [reactions, setReactions] = useState<Reaction[]>(initialReactions || []);
  const [comments, setComments] = useState<Comment[]>([]);
  const [showComments, setShowComments] = useState(autoOpen);
  const [newComment, setNewComment] = useState('');
  const [newCommentMentions, setNewCommentMentions] = useState<string[]>([]);
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [replyContent, setReplyContent] = useState('');
  const [replyMentions, setReplyMentions] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [resolvedPatientId, setResolvedPatientId] = useState<string | null>(patientId || null);

  // Lazy loading state - use initial counts from props if available
  const [reactionsCount, setReactionsCount] = useState(initialReactionsCount ?? 0);
  const [commentsCount, setCommentsCount] = useState(initialCommentsCount ?? 0);
  // Use ref to track if counts were provided initially (computed once on mount)
  const hasInitialCounts = useRef(initialReactionsCount !== undefined && initialCommentsCount !== undefined);
  const [dataLoaded, setDataLoaded] = useState(false);

  // Use ref to track if reactions were provided initially
  const hasInitialReactions = useRef(!!initialReactions);

  // Edit state
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');

  // Delete confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [commentToDelete, setCommentToDelete] = useState<string | null>(null);

  // Highlight animation state - stops after 2 seconds
  const [isAnimatingHighlight, setIsAnimatingHighlight] = useState(true);

  useEffect(() => {
    if (highlightCommentId) {
      setIsAnimatingHighlight(true);
      const timer = setTimeout(() => {
        setIsAnimatingHighlight(false);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [highlightCommentId]);

  // Initial load: only fetch counts if not provided via props
  useEffect(() => {
    if (!hasInitialCounts.current) {
      fetchCounts();
    }
  }, [executionId]);

  // Load full data and subscribe only when expanded or autoOpen
  useEffect(() => {
    if ((showComments || autoOpen) && !dataLoaded) {
      fetchReactionsAndComments();
      setDataLoaded(true);
    }
  }, [showComments, autoOpen, dataLoaded]);

  // Subscribe to realtime updates only WHILE the user is viewing comments
  // (avoids 1 open channel per execution in the feed → big DB savings)
  const subscriptionActiveRef = useRef(false);
  useEffect(() => {
    if (!dataLoaded) return;
    if (!(showComments || autoOpen)) return;
    if (subscriptionActiveRef.current) return;

    const channel = supabase
      .channel(`task-${executionId}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'task_reactions',
        filter: `task_execution_id=eq.${executionId}`
      }, () => {
        fetchReactions();
      })
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'task_comments',
        filter: `task_execution_id=eq.${executionId}`
      }, () => {
        fetchComments();
      })
      .subscribe();

    subscriptionActiveRef.current = true;

    return () => {
      supabase.removeChannel(channel);
      subscriptionActiveRef.current = false;
    };
  }, [executionId, dataLoaded, showComments, autoOpen]);

  // Fetch patient ID and team members when comments are shown
  useEffect(() => {
    if (showComments && !resolvedPatientId) {
      fetchPatientId();
    }
  }, [showComments, resolvedPatientId]);

  useEffect(() => {
    if (resolvedPatientId) {
      fetchTeamMembers();
    }
  }, [resolvedPatientId]);

  const fetchPatientId = async () => {
    if (patientId) {
      setResolvedPatientId(patientId);
      return;
    }

    const { data } = await supabase
      .from('task_executions')
      .select('patient_id')
      .eq('id', executionId)
      .maybeSingle();

    if (data?.patient_id) {
      setResolvedPatientId(data.patient_id);
    }
  };

  const fetchTeamMembers = async () => {
    if (!resolvedPatientId) return;

    const { data: links } = await supabase
      .from('patient_user_links')
      .select('user_id')
      .eq('patient_id', resolvedPatientId);

    if (!links || links.length === 0) {
      setTeamMembers([]);
      return;
    }

    const userIds = links.map(l => l.user_id);

    const { data: profiles } = await supabase
      .from('profiles')
      .select('user_id, full_name, avatar_url')
      .in('user_id', userIds);

    setTeamMembers(profiles || []);
  };

  // Lightweight count fetch for initial load
  const fetchCounts = async () => {
    const [reactionsResult, commentsResult] = await Promise.all([
      supabase
        .from('task_reactions')
        .select('id', { count: 'exact', head: true })
        .eq('task_execution_id', executionId),
      supabase
        .from('task_comments')
        .select('id', { count: 'exact', head: true })
        .eq('task_execution_id', executionId)
    ]);

    setReactionsCount(reactionsResult.count || 0);
    setCommentsCount(commentsResult.count || 0);
    if (onCommentsCountChange) {
      onCommentsCountChange(commentsResult.count || 0);
    }
  };

  const fetchReactionsAndComments = async () => {
    await Promise.all([fetchReactions(), fetchComments()]);
  };

  const fetchReactions = async () => {
    const { data } = await supabase
      .from('task_reactions')
      .select('id, emoji, user_id')
      .eq('task_execution_id', executionId);

    setReactions(data || []);
  };

  const fetchComments = async () => {
    const { data: commentsData } = await supabase
      .from('task_comments')
      .select('id, content, user_id, parent_id, created_at, updated_at, mentions')
      .eq('task_execution_id', executionId)
      .order('created_at', { ascending: true });

    if (!commentsData || commentsData.length === 0) {
      setComments([]);
      return;
    }

    const userIds = [...new Set(commentsData.map(c => c.user_id))];
    const { data: profiles } = await supabase
      .from('profiles')
      .select('user_id, full_name, avatar_url')
      .in('user_id', userIds);

    const profilesMap = new Map(profiles?.map(p => [p.user_id, p]) || []);

    const commentsWithProfiles = commentsData.map(c => ({
      ...c,
      profile: profilesMap.get(c.user_id) || { full_name: null, avatar_url: null },
      replies: [] as Comment[]
    }));

    const rootComments: Comment[] = [];
    const repliesMap = new Map<string, Comment[]>();

    commentsWithProfiles.forEach(comment => {
      if (comment.parent_id) {
        const existing = repliesMap.get(comment.parent_id) || [];
        existing.push(comment);
        repliesMap.set(comment.parent_id, existing);
      } else {
        rootComments.push(comment);
      }
    });

    rootComments.forEach(comment => {
      comment.replies = repliesMap.get(comment.id) || [];
    });

    const count = rootComments.reduce((acc, c) => acc + 1 + (c.replies?.length || 0), 0);
    setComments(rootComments);
    if (onCommentsCountChange) {
      onCommentsCountChange(count);
    }
  };

  const toggleReaction = async (emoji: string) => {
    if (!user) return;

    // Load reactions if not yet loaded (lazy loading case)
    let currentReactions = reactions;
    if (!dataLoaded && !hasInitialReactions.current) {
      const { data } = await supabase
        .from('task_reactions')
        .select('id, emoji, user_id')
        .eq('task_execution_id', executionId);

      currentReactions = data || [];
      setReactions(currentReactions);
    }

    const existingReaction = currentReactions.find(
      r => r.emoji === emoji && r.user_id === user.id
    );

    if (existingReaction) {
      const { error } = await supabase
        .from('task_reactions')
        .delete()
        .eq('id', existingReaction.id)
        .eq('user_id', user.id);

      if (error) {
        console.error('Error removing reaction:', error);
        toast.error('Erro ao remover reação');
        return;
      }

      setReactions(prev => prev.filter(r => r.id !== existingReaction.id));
      setReactionsCount(prev => Math.max(0, prev - 1));
    } else {
      const { data, error } = await supabase
        .from('task_reactions')
        .insert({
          task_execution_id: executionId,
          user_id: user.id,
          emoji
        })
        .select('id, emoji, user_id')
        .single();

      if (error) {
        console.error('Error adding reaction:', error);
        toast.error('Erro ao adicionar reação');
        return;
      }

      if (data) {
        setReactions(prev => [...prev, data]);
        setReactionsCount(prev => prev + 1);

        // PostHog Tracking
        captureEvent('task_reaction_added', {
          execution_id: executionId,
          patient_id: resolvedPatientId,
          emoji
        });
      }
    }
  };


  const addComment = async () => {
    if (!user || !newComment.trim()) return;
    setIsLoading(true);

    try {
      const { error } = await supabase
        .from('task_comments')
        .insert({
          task_execution_id: executionId,
          user_id: user.id,
          content: newComment.trim(),
          mentions: newCommentMentions.length > 0 ? newCommentMentions : null
        });

      if (error) throw error;
      setNewComment('');
      setNewCommentMentions([]);

      // PostHog Tracking
      captureEvent('task_comment_added', {
        execution_id: executionId,
        patient_id: resolvedPatientId,
        is_reply: false,
        has_mentions: newCommentMentions.length > 0
      });
    } catch (error) {

      toast.error('Erro ao adicionar comentário');
    } finally {
      setIsLoading(false);
    }
  };

  const addReply = async (parentId: string) => {
    if (!user || !replyContent.trim()) return;
    setIsLoading(true);

    try {
      const { error } = await supabase
        .from('task_comments')
        .insert({
          task_execution_id: executionId,
          user_id: user.id,
          content: replyContent.trim(),
          parent_id: parentId,
          mentions: replyMentions.length > 0 ? replyMentions : null
        });

      if (error) throw error;
      setReplyContent('');
      setReplyMentions([]);
      setReplyingTo(null);

      // PostHog Tracking
      captureEvent('task_comment_added', {
        execution_id: executionId,
        patient_id: resolvedPatientId,
        is_reply: true,
        parent_id: parentId,
        has_mentions: replyMentions.length > 0
      });
    } catch (error) {

      toast.error('Erro ao adicionar resposta');
    } finally {
      setIsLoading(false);
    }
  };

  const startEditing = (comment: Comment) => {
    setEditingCommentId(comment.id);
    setEditContent(comment.content);
    setReplyingTo(null);
    setReplyContent('');
  };

  const cancelEditing = () => {
    setEditingCommentId(null);
    setEditContent('');
  };

  const saveEdit = async (commentId: string) => {
    if (!user || !editContent.trim()) return;
    setIsLoading(true);

    try {
      const { error } = await supabase
        .from('task_comments')
        .update({ content: editContent.trim() })
        .eq('id', commentId)
        .eq('user_id', user.id);

      if (error) throw error;

      toast.success('Comentário atualizado');
      setEditingCommentId(null);
      setEditContent('');
    } catch (error) {
      toast.error('Erro ao atualizar comentário');
    } finally {
      setIsLoading(false);
    }
  };

  const confirmDelete = (commentId: string) => {
    setCommentToDelete(commentId);
    setDeleteDialogOpen(true);
  };

  const deleteComment = async () => {
    if (!user || !commentToDelete) return;
    setIsLoading(true);

    try {
      await supabase
        .from('task_comments')
        .delete()
        .eq('parent_id', commentToDelete);

      const { error } = await supabase
        .from('task_comments')
        .delete()
        .eq('id', commentToDelete)
        .eq('user_id', user.id);

      if (error) throw error;

      toast.success('Comentário excluído');
    } catch (error) {
      toast.error('Erro ao excluir comentário');
    } finally {
      setIsLoading(false);
      setDeleteDialogOpen(false);
      setCommentToDelete(null);
    }
  };

  const groupedReactions = AVAILABLE_EMOJIS.map(emoji => ({
    emoji,
    count: reactions.filter(r => r.emoji === emoji).length,
    hasUserReacted: reactions.some(r => r.emoji === emoji && r.user_id === user?.id)
  })).filter(r => r.count > 0 || !compact);

  // Use reactionsCount from state when data hasn't been loaded yet (for initial display)
  // If we have initial reactions (array), we rely on that length instead of the count prop
  const totalReactionsFromData = reactions.length;
  const displayReactionsCount = (dataLoaded || hasInitialReactions.current) ? totalReactionsFromData : reactionsCount;

  // Use commentsCount from state when data hasn't been loaded yet
  const totalCommentsFromData = comments.reduce((acc, c) => acc + 1 + (c.replies?.length || 0), 0);
  const totalComments = dataLoaded ? totalCommentsFromData : commentsCount;

  const renderComment = (comment: Comment, isReply = false) => {
    const isOwner = user?.id === comment.user_id;
    const isEditing = editingCommentId === comment.id;
    const isEdited = comment.updated_at && comment.created_at !== comment.updated_at;
    const isHighlightedComment = highlightCommentId === comment.id;

    return (
      <div
        key={comment.id}
        className={cn(
          "flex gap-2 transition-all duration-500",
          isHighlightedComment && "ring-2 ring-primary ring-offset-2 rounded-lg p-1",
          isHighlightedComment && isAnimatingHighlight && "animate-pulse"
        )}
      >
        {isReply && <CornerDownRight className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-1" />}
        <SignedAvatar
          src={comment.profile?.avatar_url}
          bucket="avatars"
          className={cn("flex-shrink-0", isReply ? "w-6 h-6" : "w-7 h-7")}
          fallback={
            <span className="bg-muted text-xs w-full h-full flex items-center justify-center rounded-full">
              {comment.profile?.full_name?.charAt(0) || '?'}
            </span>
          }
        />
        <div className="flex-1 min-w-0">
          {isEditing ? (
            <div className="flex gap-2">
              <Input
                value={editContent}
                onChange={(e) => setEditContent(e.target.value)}
                className="flex-1 h-9 text-sm"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    saveEdit(comment.id);
                  }
                  if (e.key === 'Escape') {
                    cancelEditing();
                  }
                }}
              />
              <Button
                size="sm"
                onClick={() => saveEdit(comment.id)}
                disabled={isLoading || !editContent.trim()}
                className="h-9 px-2"
              >
                <Check className="w-4 h-4" />
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={cancelEditing}
                className="h-9 px-2"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          ) : (
            <>
              <div className={cn(
                "rounded-lg px-3 py-2 group relative",
                isReply ? "bg-muted/50" : "bg-muted",
                isHighlightedComment && "bg-primary/10"
              )}>
                <p className="text-xs font-medium">{comment.profile?.full_name || 'Usuário'}</p>
                <p className="text-sm">{renderCommentWithMentions(comment.content, user?.id)}</p>

                {isOwner && (
                  <div className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                    <button
                      onClick={() => startEditing(comment)}
                      className="p-1 rounded hover:bg-background/50 text-muted-foreground hover:text-foreground"
                      title="Editar comentário"
                    >
                      <Pencil className="w-3 h-3" />
                    </button>
                    <button
                      onClick={() => confirmDelete(comment.id)}
                      className="p-1 rounded hover:bg-destructive/20 text-muted-foreground hover:text-destructive"
                      title="Excluir comentário"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-3 mt-1 px-1">
                <span className="text-xs text-muted-foreground">
                  {format(new Date(comment.created_at), "dd/MM HH:mm", { locale: ptBR })}
                  {isEdited && <span className="ml-1 italic">(editado)</span>}
                </span>
                {!isReply && (
                  <button
                    onClick={() => setReplyingTo(replyingTo === comment.id ? null : comment.id)}
                    className="text-xs text-primary hover:underline"
                  >
                    Responder
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-2">
      {/* Reactions Row */}
      <div className="flex items-center gap-1 flex-wrap">
        {!dataLoaded && !hasInitialReactions.current && displayReactionsCount > 0 ? (
          <button
            onClick={() => {
              if (!showComments) setShowComments(true);
            }}
            className="flex items-center gap-1 px-2 py-1 rounded-full text-sm transition-all bg-primary/10 text-primary border border-primary/20 hover:bg-primary/20"
          >
            <SmilePlus className="w-4 h-4" />
            <span className="text-xs font-medium">{displayReactionsCount}</span>
          </button>
        ) : (
          groupedReactions.map(({ emoji, count, hasUserReacted }) => (
            <button
              key={emoji}
              onClick={() => toggleReaction(emoji)}
              className={cn(
                "flex items-center gap-1 px-2 py-1 rounded-full text-sm transition-all",
                hasUserReacted
                  ? "bg-primary/20 border border-primary"
                  : "bg-muted hover:bg-muted/80 border border-transparent",
                count === 0 && "opacity-50"
              )}
            >
              <span>{emoji}</span>
              {count > 0 && <span className="text-xs font-medium">{count}</span>}
            </button>
          ))
        )}

        {/* Comment toggle button */}
        <button
          onClick={() => setShowComments(!showComments)}
          className={cn(
            "flex items-center gap-1 px-2 py-1 rounded-full text-sm transition-all ml-auto",
            showComments
              ? "bg-primary/20 text-primary"
              : totalComments > 0
                ? "bg-success/20 text-success border border-success/30"
                : "bg-muted hover:bg-muted/80"
          )}
        >
          <MessageCircle className={cn("w-4 h-4", totalComments > 0 && !showComments && "text-success")} />
          {totalComments > 0 && <span className="text-xs font-medium">{totalComments}</span>}
        </button>
      </div>

      {/* Comments Section */}
      {showComments && (
        <div className="space-y-3 pt-2 border-t">
          {/* Existing Comments */}
          {comments.map(comment => (
            <div key={comment.id} className="space-y-2">
              {renderComment(comment)}

              {comment.replies && comment.replies.length > 0 && (
                <div className="ml-6 space-y-2">
                  {comment.replies.map(reply => renderComment(reply, true))}
                </div>
              )}

              {replyingTo === comment.id && (
                <div className="ml-6 flex gap-2 items-end">
                  <CornerDownRight className="w-4 h-4 text-muted-foreground flex-shrink-0 mb-3" />
                  <MentionInput
                    value={replyContent}
                    onChange={setReplyContent}
                    onMentionsChange={setReplyMentions}
                    teamMembers={teamMembers}
                    currentUserId={user?.id}
                    placeholder="Escreva uma resposta... Use @ para mencionar"
                    className="flex-1"
                    onSubmit={() => addReply(comment.id)}
                    onCancel={() => {
                      setReplyingTo(null);
                      setReplyContent('');
                      setReplyMentions([]);
                    }}
                    autoFocus
                    disabled={isLoading}
                  />
                  <Button
                    size="icon"
                    onClick={() => addReply(comment.id)}
                    disabled={isLoading || !replyContent.trim()}
                    className="h-10 w-10 shrink-0"
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => {
                      setReplyingTo(null);
                      setReplyContent('');
                      setReplyMentions([]);
                    }}
                    className="h-10 w-10 shrink-0"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>
          ))}

          {/* New Comment Input */}
          <div className="flex gap-2 items-end">
            <MentionInput
              value={newComment}
              onChange={setNewComment}
              onMentionsChange={setNewCommentMentions}
              teamMembers={teamMembers}
              currentUserId={user?.id}
              placeholder="Adicionar comentário... Use @ para mencionar"
              className="flex-1"
              onSubmit={addComment}
              disabled={isLoading}
            />
            <Button
              size="icon"
              onClick={addComment}
              disabled={isLoading || !newComment.trim()}
              className="h-10 w-10 shrink-0"
            >
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir comentário</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir este comentário? Esta ação não pode ser desfeita.
              {comments.find(c => c.id === commentToDelete)?.replies?.length ? (
                <span className="block mt-2 text-destructive">
                  ⚠️ As respostas a este comentário também serão excluídas.
                </span>
              ) : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={deleteComment}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
